import { c as _c } from "react/compiler-runtime";
import { Form, Formik, useFormikContext } from 'formik';
import Text from '../../../../common/base/Text';
import TextInput from '../../../../common/Form/Fields/TextInput';
import SelectInput from '../../../../common/Form/Fields/SelectInput';
import Link from '../../../../common/base/Link';
import { Div } from '../../../../common/helpers/StyledUtils';
import { OutpostGetStartedContext, OutpostGetStartedStepProps } from '../GetStartedView';
import EditorInput from '../../../../common/Form/Fields/EditorInput';
import { CodeSnippetWithForm } from '../../../../common/CodeSnippet';
import { StyledCard } from '../../../../common/base/Card';
import { buildPublishEventExamples, getOutpostSnippetLanguage, useOutpostSnippetMaskableValue } from '../../utils/outpostSnippets';
import { entriesToMetadata, MetadataEntry, MetadataFields } from '../../utils/outpostForms';
import Icon from '../../../../common/base/Icon';
import CreatedResourceData from './CreatedResourceData';
import { capitalizeFirstLetter } from '../../../../../utils';
const DEFAULT_EVENT_ID = 'evt_123';
const DEFAULT_EVENT_DATA = `{
  "hello": "world"
}`;
interface FormValues {
  id: string;
  topic: string;
  metadata_entries: MetadataEntry[];
  event_data: string;
}
const parseEventData = (event_data: string): unknown => {
  try {
    return JSON.parse(event_data);
  } catch {
    return {};
  }
};
const PublishEventForm: React.FC<{
  context: OutpostGetStartedContext;
  topic_options: {
    value: string;
    label: string;
  }[];
}> = ({
  context: {
    active_language,
    tenant,
    event,
    destination,
    test_url
  },
  topic_options
}) => {
  const {
    values
  } = useFormikContext<FormValues>();
  const has_topics = topic_options.length > 0;
  const trimmed_topic = values.topic.trim();
  const trimmed_id = values.id.trim();
  const event_metadata = entriesToMetadata(values.metadata_entries);
  const example = buildPublishEventExamples({
    tenant_id: tenant?.id ?? '',
    id: trimmed_id || undefined,
    topic: has_topics ? trimmed_topic || undefined : undefined,
    metadata: Object.keys(event_metadata).length > 0 ? event_metadata : undefined,
    data: parseEventData(values.event_data ?? '{}')
  })[active_language] ?? '';
  const snippet_language = getOutpostSnippetLanguage(active_language);
  const maskable_value = useOutpostSnippetMaskableValue();

  // Surface the Console deep-link only when the destination is actually
  // pointed at the auto-generated test URL. If the user swapped in a custom
  // URL we don't know where their events end up, so omit the link.
  const destination_url = destination?.config?.url;
  const is_using_test_url = Boolean(test_url?.ingest_url && destination_url && destination_url === test_url.ingest_url);
  const console_alt_action = is_using_test_url && test_url ? <Link primary small href={test_url.inspect_url} target="_blank" rel="noreferrer" icon="export_notes">
        View in Console
      </Link> : null;
  return <Form>
      <CodeSnippetWithForm code={example} language={snippet_language} section_title="Event properties" maskable_value={maskable_value} default_open>
        <TextInput name="id" label="Event ID" placeholder="evt_123" help="This ID will be used for the published event request and as an idempotency key." mono m={{
        b: 4
      }} />
        {has_topics && <Div m={{
        b: 4
      }}>
            <SelectInput required name="topic" label="Topic" options={topic_options} block m={0} />
          </Div>}
        <Text semi size="xs" muted m={{
        b: 1
      }}>
          Event data
          <Text as="span" danger>
            *
          </Text>
        </Text>
        <StyledCard overflow_hidden>
          <EditorInput variant="card" name="event_data" language="json" height="120px" display="minimal" />
        </StyledCard>
        <Text semi size="xs" muted m={{
        t: 4,
        b: 2
      }}>
          Metadata
        </Text>
        <Div m={{
        b: 2
      }}>
          <MetadataFields />
        </Div>

        <Text size="xs" muted flex={{
        align: 'center',
        gap: 1
      }}>
          <Icon icon="info" />
          For webhooks destinations, metadata is mapped to HTTP headers (eg. x-outpost-some-key).
        </Text>
      </CodeSnippetWithForm>
      <Div m={{
      t: 6
    }}>
        <CreatedResourceData waiting_label={active_language === 'http' ? 'Copy cURL request and send the API call via your terminal or HTTP client...' : `Run ${capitalizeFirstLetter(active_language)} command to publish an event...`} status_label="Event received" created_resource={event} alt_action={console_alt_action} />
      </Div>
    </Form>;
};
const PublishEvent: React.FC<OutpostGetStartedStepProps> = t0 => {
  const $ = _c(13);
  const {
    context
  } = t0;
  const default_topic = context.topics?.[0] ?? "";
  let t1;
  if ($[0] !== context.topics) {
    t1 = context.topics ?? [];
    $[0] = context.topics;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = t1.map(_temp);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const topic_options = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== default_topic) {
    t4 = {
      id: DEFAULT_EVENT_ID,
      topic: default_topic,
      metadata_entries: t3,
      event_data: DEFAULT_EVENT_DATA
    };
    $[5] = default_topic;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== context || $[8] !== topic_options) {
    t5 = <PublishEventForm context={context} topic_options={topic_options} />;
    $[7] = context;
    $[8] = topic_options;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <Formik enableReinitialize={true} initialValues={t4} onSubmit={_temp2}>{t5}</Formik>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
export default PublishEvent;
function _temp(topic) {
  return {
    value: topic,
    label: topic
  };
}
function _temp2() {
  return Promise.resolve();
}